// Home.js
import React from 'react';
import { Link } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, EffectFade, Pagination } from 'swiper/modules';
import { FiArrowRight, FiUsers, FiShield, FiHeart, FiLayers, FiCpu, FiLink } from 'react-icons/fi';
import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/pagination';
import hero1 from '../assets/hero1.svg';
import hero2 from '../assets/hero2.svg';
import hero3 from '../assets/hero3.svg';
import quienes from '../assets/quienes.svg';

const slides = [hero1, hero2, hero3];

const pilares = [
  { icon: FiUsers, titulo: 'Profesionalismo', desc: 'Equipo técnico calificado en ingeniería civil, eléctrica, mecánica y petrolera.' },
  { icon: FiShield, titulo: 'Compromiso', desc: 'Ejecutamos cada proyecto con responsabilidad, planificación y orientación a resultados.' },
  { icon: FiHeart, titulo: 'Confianza', desc: 'Relaciones de largo plazo con clientes públicos y privados del sector industrial.' },
  { icon: FiLayers, titulo: 'Solidez', desc: 'Experiencia y capacidad operativa desde 2010 para afrontar retos de gran escala.' },
  { icon: FiCpu, titulo: 'Innovación', desc: 'Incorporamos mejoras técnicas y soluciones adaptadas al entorno cambiante.' },
  { icon: FiLink, titulo: 'Alianzas', desc: 'Vínculos estratégicos con la industria petrolera e instituciones del Estado.' },
];

const Home = () => {
  return (
    <div className="w-full bg-white">
      {/* Hero */}
      <section className="relative w-full h-[88vh] min-h-[560px]">
        <Swiper
          modules={[Autoplay, EffectFade, Pagination]}
          effect="fade"
          loop
          speed={1200}
          autoplay={{ delay: 5000, disableOnInteraction: false }}
          pagination={{ clickable: true }}
          className="w-full h-full"
        >
          {slides.map((img, idx) => (
            <SwiperSlide key={idx}>
              <img src={img} alt={`Slide ${idx + 1}`} className="w-full h-full object-cover" />
            </SwiperSlide>
          ))}
        </Swiper>

        <div className="absolute inset-0 bg-gradient-to-b from-primary-900/80 via-primary-900/70 to-primary-900/80 z-10"></div>

        <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-center px-6 z-20">
          <p className="eyebrow !text-accent-400 mb-5 animate-fadeInUp">
            Ingeniería · Construcción · Petróleo
          </p>
          <h1 className="text-4xl md:text-6xl font-bold mb-5 drop-shadow-lg !text-white animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
            Corporación Industrial de Occidente
          </h1>
          <p className="text-lg md:text-xl max-w-2xl text-white/85 leading-relaxed animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
            Soluciones integrales de ingeniería, construcción y servicios para la industria petrolera y de infraestructura.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4 animate-fadeInUp" style={{ animationDelay: '0.3s' }}>
            <Link to="/servicios" className="btn-accent">
              Nuestros servicios
              <FiArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/contacto"
              className="inline-flex items-center gap-2 rounded-full border border-white/40 px-6 py-3 text-sm font-semibold text-white transition duration-300 hover:bg-white hover:text-primary"
            >
              Contáctanos
            </Link>
          </div>
        </div>
      </section>

      {/* Sobre Nosotros */}
      <section className="py-20 bg-white">
        <div className="container-page grid md:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <div className="absolute -inset-3 bg-gradient-to-tr from-primary-100 to-accent-100 rounded-2xl rotate-2"></div>
            <img src={quienes} alt="Corporación Industrial de Occidente" className="relative rounded-2xl shadow-card w-full h-auto object-cover" />
          </div>
          <div>
            <p className="eyebrow mb-3">Sobre nosotros</p>
            <h2 className="section-title mb-5">¿Quiénes Somos?</h2>
            <p className="text-lg leading-relaxed text-slate-600">
              Corporación Industrial de Occidente, C.A. es una empresa venezolana con más de una
              década de experiencia en el desarrollo de proyectos de arquitectura e ingeniería civil,
              eléctrica, mecánica, industrial y petrolera. Nos especializamos en la construcción de
              plantas de procesamiento de crudo, estaciones de flujo, oleoductos, gaseoductos y tanques
              de almacenamiento, así como en servicios petroleros, movimiento de tierra, vialidad,
              metalmecánica y transporte.
            </p>
            <Link to="/nosotros" className="btn-primary mt-7">
              Conócenos más
              <FiArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Pilares */}
      <section className="py-20 bg-slate-50">
        <div className="container-page">
          <div className="text-center mb-14">
            <p className="eyebrow mb-3">Lo que nos distingue</p>
            <h2 className="section-title">Nuestros Pilares</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pilares.map((p) => (
              <div
                key={p.titulo}
                className="group rounded-2xl bg-white p-8 shadow-card transition duration-300 hover:-translate-y-1 hover:shadow-soft"
              >
                <div className="w-14 h-14 rounded-xl bg-primary-50 text-primary flex items-center justify-center mb-5 transition group-hover:bg-primary group-hover:text-white">
                  <p.icon className="w-7 h-7" />
                </div>
                <h3 className="text-xl font-semibold mb-2">{p.titulo}</h3>
                <p className="text-sm leading-relaxed text-slate-600">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
